import { useEffect, useState } from "react";
import http, { formatApiErrorDetail } from "@/api";
import { Tags, Plus, Pencil, X, Save } from "lucide-react";

export default function CategoriesManager() {
  const [items, setItems] = useState([]);
  const [original, setOriginal] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = () => http.get("/categories").then((r) => {
    setItems(r.data.items || []);
    setOriginal(r.data.items || []);
  });
  useEffect(() => { load().catch(() => {}); }, []);

  const add = () => {
    const c = newCat.trim();
    if (!c || items.some((x) => x.toLowerCase() === c.toLowerCase())) return;
    setItems([...items, c]);
    setNewCat("");
  };

  const remove = (c) => setItems(items.filter((x) => x !== c));

  const rename = async (c) => {
    const nuevo = window.prompt(`Renombrar categoría "${c}" a:`, c);
    if (!nuevo || nuevo.trim() === c) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await http.post("/admin/categories/rename", { old: c, new: nuevo.trim() });
      setMsg(`Renombrada: ${r.data.suppliers_updated} proveedores, ${r.data.users_updated} usuarios y ${r.data.kpis_updated} KPIs migrados.`);
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await http.put("/admin/categories", { items });
      setItems(r.data.items); setOriginal(r.data.items);
      setMsg("Categorías guardadas.");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      await load();
    } finally { setBusy(false); }
  };

  const dirty = JSON.stringify(items) !== JSON.stringify(original);

  return (
    <section className="sketch-border bg-[var(--cream-soft)] p-6 mb-6" data-testid="categories-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
            <Tags size={16} /> categorías de compra
          </div>
          <h2 className="font-display text-2xl">Taxonomía de categorías</h2>
          <p className="text-sm text-[var(--ink-soft)] mt-1 max-w-2xl">
            Agrega, renombra o elimina categorías (Empaques, Ingredientes, Maquilas Co-Man, CAPEX, MRO…).
            Renombrar migra automáticamente proveedores, usuarios y KPIs. No puedes eliminar una categoría en uso.
          </p>
        </div>
        <button onClick={save} disabled={busy || !dirty} className="btn-primary text-sm inline-flex items-center gap-2"
          data-testid="save-categories-btn">
          <Save size={14} /> Guardar cambios
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mt-4" data-testid="categories-list">
        {items.map((c) => (
          <span key={c} className="inline-flex items-center gap-1.5 bg-white border border-[var(--line)] rounded-full px-3 py-1.5 text-sm"
            data-testid={`category-chip-${c.replace(/\s+/g, "-")}`}>
            {c}
            <button onClick={() => rename(c)} title="Renombrar (migra datos)" disabled={busy}
              className="text-[var(--lala-blue)] hover:opacity-70" data-testid={`rename-category-${c.replace(/\s+/g, "-")}`}>
              <Pencil size={12} />
            </button>
            <button onClick={() => remove(c)} title="Quitar de la lista" disabled={busy}
              className="text-red-600 hover:opacity-70" data-testid={`remove-category-${c.replace(/\s+/g, "-")}`}>
              <X size={12} />
            </button>
          </span>
        ))}
      </div>

      <div className="flex gap-2 mt-4 max-w-md">
        <input type="text" value={newCat} onChange={(e) => setNewCat(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Nueva categoría (ej. Maquilas Co-Pack, CAPEX, MRO)…"
          className="input-field flex-1" data-testid="new-category-input" />
        <button onClick={add} className="sketch-pill bg-white border border-[var(--lala-blue)] text-[var(--lala-blue)] px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1"
          data-testid="add-category-btn">
          <Plus size={14} /> Agregar
        </button>
      </div>

      {msg && <div className="mt-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-2.5" data-testid="categories-msg">{msg}</div>}
      {err && <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid="categories-err">{err}</div>}
    </section>
  );
}
